import { useState } from 'react';
import { Info } from 'lucide-react';

const TOOLS = [
  {
    key: 'phactboost',
    name: 'PhactBoost',
    desc: 'Gradient-boosted ensemble combining 20+ variant features for pathogenicity prediction.',
    threshold: { low: 0.3, high: 0.7 },
  },
  {
    key: 'alphamissense',
    name: 'AlphaMissense',
    desc: 'DeepMind pathogenicity classifier based on AlphaFold protein structure embeddings.',
    threshold: { low: 0.34, high: 0.564 },
  },
  {
    key: 'gpnMsa',
    name: 'GPN-MSA',
    desc: 'Genomic Pre-trained Network using multiple sequence alignment for conservation scoring.',
    threshold: { low: 0.3, high: 0.7 },
  },
  {
    key: 'dbscSNV_ada',
    name: 'dbscSNV Ada',
    desc: 'AdaBoost ensemble for splice site variant prediction.',
    threshold: { low: 0.6, high: 0.6 },
  },
  {
    key: 'dbscSNV_rf',
    name: 'dbscSNV RF',
    desc: 'Random forest model for splice site variant prediction.',
    threshold: { low: 0.6, high: 0.6 },
  },
  {
    key: 'revel',
    name: 'REVEL',
    desc: 'Rare Exome Variant Ensemble Learner — meta-predictor integrating 13 tools.',
    threshold: { low: 0.5, high: 0.75 },
  },
];

function scoreColor(score, threshold) {
  if (score >= threshold.high) return { bg: '#fecaca', text: '#991b1b', bar: '#ef4444', label: 'Pathogenic' };
  if (score >= threshold.low) return { bg: '#fef3c7', text: '#92400e', bar: '#f59e0b', label: 'Uncertain' };
  return { bg: '#dcfce7', text: '#14532d', bar: '#22c55e', label: 'Benign' };
}

function ScoreCard({ toolDef, scoreData }) {
  const [showTip, setShowTip] = useState(false);
  if (!scoreData) return null;

  const score = scoreData.score ?? scoreData.max;
  const label = scoreData.label;
  const c = scoreColor(score, toolDef.threshold);
  const pct = Math.round(score * 100);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 card-hover shadow-card flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-gray-900 text-sm">{toolDef.name}</div>
          <span
            className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: c.bg, color: c.text }}
          >
            {label}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-2xl font-extrabold tabular-nums" style={{ color: c.bar }}>
            {score.toFixed(3)}
          </span>
          <div className="relative">
            <button
              className="text-gray-400 hover:text-gray-600 transition-colors"
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
              aria-label={`About ${toolDef.name}`}
            >
              <Info size={14} />
            </button>
            {showTip && (
              <div className="absolute right-0 bottom-6 w-48 bg-gray-900 text-white text-xs rounded-lg p-2.5 z-20 shadow-xl leading-relaxed">
                {toolDef.desc}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="score-bar-gradient absolute inset-y-0 left-0 w-full rounded-full opacity-20" />
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: c.bar }}
        />
      </div>

      <div className="flex justify-between text-xs text-gray-400">
        <span>Benign</span>
        <span>Pathogenic</span>
      </div>
    </div>
  );
}

function SpliceAICard({ data }) {
  const [showTip, setShowTip] = useState(false);
  const deltas = [
    { key: 'ds_ag', label: 'DS_AG', name: 'Acceptor Gain' },
    { key: 'ds_al', label: 'DS_AL', name: 'Acceptor Loss' },
    { key: 'ds_dg', label: 'DS_DG', name: 'Donor Gain' },
    { key: 'ds_dl', label: 'DS_DL', name: 'Donor Loss' },
  ];
  const maxScore = data.max;
  const c = maxScore >= 0.5 ? { bg: '#fecaca', text: '#991b1b', bar: '#ef4444' }
    : maxScore >= 0.2 ? { bg: '#fef3c7', text: '#92400e', bar: '#f59e0b' }
    : { bg: '#dcfce7', text: '#14532d', bar: '#22c55e' };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 card-hover shadow-card flex flex-col gap-3 col-span-1">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-gray-900 text-sm">SpliceAI</div>
          <span
            className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: c.bg, color: c.text }}
          >
            {data.label} splice impact
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-2xl font-extrabold tabular-nums" style={{ color: c.bar }}>
            {maxScore.toFixed(3)}
          </span>
          <div className="relative">
            <button
              className="text-gray-400 hover:text-gray-600 transition-colors"
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
            >
              <Info size={14} />
            </button>
            {showTip && (
              <div className="absolute right-0 bottom-6 w-52 bg-gray-900 text-white text-xs rounded-lg p-2.5 z-20 shadow-xl leading-relaxed">
                Deep learning model predicting splicing from raw DNA. Shows four delta scores — acceptor/donor gain/loss.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {deltas.map(({ key, label, name }) => {
          const val = data[key];
          const pct = Math.round(val * 100);
          const dc = val >= 0.5 ? '#ef4444' : val >= 0.2 ? '#f59e0b' : '#22c55e';
          return (
            <div key={key} className="bg-gray-50 rounded-lg p-2.5">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-mono font-semibold text-gray-700">{label}</span>
                <span className="text-xs font-bold tabular-nums" style={{ color: dc }}>{val.toFixed(3)}</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: dc }} />
              </div>
              <div className="text-xs text-gray-400 mt-1">{name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PathogenicityScores({ scores }) {
  return (
    <section id="scores" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: '#021f30' }}>
        <h3 className="text-white font-bold text-base">Pathogenicity Scores</h3>
        <span className="text-white/50 text-xs">7 tools evaluated</span>
      </div>

      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {TOOLS.map((tool) => (
          <ScoreCard key={tool.key} toolDef={tool} scoreData={scores[tool.key]} />
        ))}
        <SpliceAICard data={scores.spliceAI} />
      </div>
    </section>
  );
}
